import { useEffect, useMemo, useState } from "react";

import { useIndexStatus, useProjectSearch } from "../hooks/useProjects";
import {
  useSubscribeProject,
  useSubscriptions,
  useUnsubscribeProject,
} from "../hooks/useSubscriptions";
import type { ProjectSearchRow, SearchFilters } from "../api/types";

interface ProjectPickerProps {
  selectedProjects: ProjectSearchRow[];
  onSelectionChange: (projects: ProjectSearchRow[]) => void;
  filters?: SearchFilters;
}

export function ProjectPicker({
  selectedProjects,
  onSelectionChange,
  filters,
}: ProjectPickerProps) {
  const [query, setQuery] = useState("");
  const [toast, setToast] = useState<string | null>(null);

  const subscriptionsQuery = useSubscriptions();
  const indexStatus = useIndexStatus();
  const searchQuery = useProjectSearch(query, filters);
  const subscribeMutation = useSubscribeProject();
  const unsubscribeMutation = useUnsubscribeProject();

  useEffect(() => {
    if (!toast) {
      return;
    }
    const timer = window.setTimeout(() => setToast(null), 4000);
    return () => window.clearTimeout(timer);
  }, [toast]);

  const subscriptions = subscriptionsQuery.data?.subscriptions ?? [];
  const subscribedIds = useMemo(
    () => new Set(subscriptions.map((s) => s.project_id)),
    [subscriptions],
  );
  const selectedIds = useMemo(
    () => new Set(selectedProjects.map((p) => p.id)),
    [selectedProjects],
  );

  const normalizedQuery = query.trim().toLowerCase();
  const subscribedSection: ProjectSearchRow[] = useMemo(() => {
    const matching = normalizedQuery
      ? subscriptions.filter(
          (s) =>
            (s.project_code ?? "").toLowerCase().includes(normalizedQuery) ||
            s.name.toLowerCase().includes(normalizedQuery) ||
            s.division.toLowerCase().includes(normalizedQuery),
        )
      : subscriptions;
    return matching.map((s) => ({
      id: s.project_id,
      project_code: s.project_code,
      name: s.name,
      division: s.division,
      year: s.year,
      path: s.path,
      subscribed: true,
      parse_ok: true,
    }));
  }, [subscriptions, normalizedQuery]);

  const searchResults = searchQuery.data?.results ?? [];
  const allResultsSection = useMemo(
    () => searchResults.filter((r) => !subscribedIds.has(r.id)),
    [searchResults, subscribedIds],
  );

  const isDemoIndex = indexStatus.data?.data_source === "demo";
  const isFiltered = Boolean(
    filters &&
      (filters.divisions.length || filters.years.length || filters.pathPrefix),
  );

  function toggleSelected(row: ProjectSearchRow): void {
    if (selectedIds.has(row.id)) {
      onSelectionChange(selectedProjects.filter((p) => p.id !== row.id));
    } else {
      onSelectionChange([...selectedProjects, row]);
    }
  }

  function handleToggleSubscribed(row: ProjectSearchRow): void {
    if (subscribedIds.has(row.id)) {
      unsubscribeMutation.mutate(row.id);
      return;
    }
    subscribeMutation.mutate(row.id, {
      onError: (error) => {
        setToast(
          error.code === "NO_EGNYTE_ACCESS"
            ? "No Egnyte access to this project folder."
            : error.message,
        );
      },
    });
  }

  return (
    <div className="flex flex-col gap-3">
      <div>
        <label
          htmlFor="project-search"
          className="mb-1 block text-sm font-medium text-slate-700"
        >
          Project
        </label>
        <input
          id="project-search"
          type="search"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Search by code, name, or division…"
          className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
        />
      </div>

      {selectedProjects.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {selectedProjects.map((project) => (
            <span
              key={project.id}
              className="inline-flex items-center gap-1 rounded-full bg-blue-50 py-1 pl-2.5 pr-1 text-xs font-medium text-blue-800"
            >
              {project.project_code ?? project.name}
              <button
                type="button"
                onClick={() => toggleSelected(project)}
                aria-label={`Remove ${project.name} from selection`}
                className="rounded-full px-1 text-blue-600 hover:bg-blue-100"
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}

      <div className="max-h-64 overflow-y-auto rounded-md border border-slate-200">
        <ProjectSection
          title="Subscribed"
          rows={subscribedSection}
          selectedIds={selectedIds}
          onToggleSelected={toggleSelected}
          onToggleSubscribed={handleToggleSubscribed}
        />
        <ProjectSection
          title="All results"
          rows={allResultsSection}
          selectedIds={selectedIds}
          onToggleSelected={toggleSelected}
          onToggleSubscribed={handleToggleSubscribed}
        />
        {searchQuery.isLoading && (
          <p className="px-3 py-2 text-sm text-slate-400">Searching…</p>
        )}
        {!searchQuery.isLoading &&
          subscribedSection.length === 0 &&
          allResultsSection.length === 0 && (
            <EmptyState
              query={normalizedQuery}
              isFiltered={isFiltered}
              isDemoIndex={isDemoIndex}
            />
          )}
      </div>

      {isDemoIndex && (
        <p className="rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-800">
          This index holds sample data only — it is not connected to Egnyte
          yet, so real project names will not be found.
        </p>
      )}

      {toast && (
        <p
          role="alert"
          className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700"
        >
          {toast}
        </p>
      )}
    </div>
  );
}

/**
 * The empty list means three different things, and conflating them is what made
 * "not found" read as a mystery: an untouched picker with no favourites, a real
 * miss, and a miss caused by a scope filter the user set in another tab.
 */
function EmptyState({
  query,
  isFiltered,
  isDemoIndex,
}: {
  query: string;
  isFiltered: boolean;
  isDemoIndex: boolean;
}) {
  if (!query) {
    return (
      <p className="px-3 py-2 text-sm text-slate-400">
        Search for a project, or star one to keep it here.
      </p>
    );
  }
  return (
    <div className="px-3 py-2 text-sm text-slate-400">
      <p>No projects match &ldquo;{query}&rdquo;.</p>
      {isFiltered && !isDemoIndex && (
        <p className="mt-1 text-xs">
          Your search scope is narrowed — check Settings.
        </p>
      )}
    </div>
  );
}

interface ProjectSectionProps {
  title: string;
  rows: ProjectSearchRow[];
  selectedIds: Set<number>;
  onToggleSelected: (row: ProjectSearchRow) => void;
  onToggleSubscribed: (row: ProjectSearchRow) => void;
}

function ProjectSection({
  title,
  rows,
  selectedIds,
  onToggleSelected,
  onToggleSubscribed,
}: ProjectSectionProps) {
  if (rows.length === 0) {
    return null;
  }

  return (
    <div>
      <p className="sticky top-0 bg-slate-50 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
        {title}
      </p>
      <ul>
        {rows.map((row) => (
          <li
            key={row.id}
            className="flex items-center gap-2 border-t border-slate-100 px-3 py-2"
          >
            <input
              type="checkbox"
              id={`project-${row.id}`}
              checked={selectedIds.has(row.id)}
              onChange={() => onToggleSelected(row)}
              className="h-4 w-4 rounded border-slate-300 text-blue-700 focus:ring-blue-500"
            />
            <label
              htmlFor={`project-${row.id}`}
              className="flex-1 cursor-pointer text-sm"
            >
              <span className="font-medium text-slate-900">
                {row.project_code ?? row.name}
              </span>
              {row.project_code && (
                <span className="ml-1.5 text-slate-500">{row.name}</span>
              )}
              <span className="ml-1.5 inline-flex items-center rounded bg-slate-100 px-1.5 py-0.5 text-[11px] text-slate-600">
                {row.year ? `${row.division} · ${row.year}` : row.division}
              </span>
              {!row.parse_ok && (
                <span className="ml-1.5 inline-flex items-center rounded bg-amber-100 px-1.5 py-0.5 text-[11px] text-amber-700">
                  unparsed
                </span>
              )}
            </label>
            <button
              type="button"
              onClick={() => onToggleSubscribed(row)}
              aria-pressed={row.subscribed}
              aria-label={
                row.subscribed
                  ? `Unsubscribe from ${row.name}`
                  : `Subscribe to ${row.name}`
              }
              className={`text-lg leading-none ${
                row.subscribed
                  ? "text-amber-500"
                  : "text-slate-300 hover:text-amber-400"
              }`}
            >
              {row.subscribed ? "★" : "☆"}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
